// All user-facing English copy lives here so it can be tuned in one place.
// Voice: playful, confident, slightly deadpan. The world (see LORE.md) is a
// glass hall in the sky — keep venue language ("halls", "opened", "visitors")
// rather than app language ("features", "modules").
export const copy = {
  nav: {
    playground: "The Atrium",
    lab: "The Halls",
    about: "About",
  },
  hero: {
    title: "Poparium",
    tagline: "A glass hall in the sky where the world comes to pop things.",
    sub: "Float in. Pop something. Feel better.",
    cta: "Start popping",
    scrollCue: "scroll",
    tallyLabel: "pops, worldwide, since the doors opened",
    liveMany: "{n} poppers in the hall right now",
    liveAlone: "the hall is yours right now",
    countdown: "{n} to {next} — push",
    standings: "the standings are open →",
    daily: "Daily #{n} — today's sky is waiting →",
    dailyDone: "Daily #{n}",
  },
  playground: {
    kicker: "The Balloon Atrium",
    title: "Balloons. Infinite. Poppable.",
    body: "The Atrium never runs out. Click, tap, pop — confetti flies, a satisfying pop sounds off, and your tally joins the Grand Tally: every pop, by every visitor, since the doors opened.",
    loading: "Inflating balloons…",
    counterLabel: "pops worldwide",
  },
  lab: {
    kicker: "The Halls",
    title: "Rooms that shouldn't exist",
    body: "Poparium keeps opening new halls. Some are closer to games, some are closer to weather. The rest are still behind scaffolding.",
    items: {
      dreamlab: {
        title: "The Dream Wing",
        blurb:
          "Drift through three impossible skies: a field that blooms, a ceiling of soap film, and a nebula that inhales and bursts.",
        badge: "open",
      },
      observatory: {
        title: "The Observatory",
        blurb:
          "Instruments pointed at the Grand Tally itself — live readings, the last two hours of activity, and every milestone the number has crossed.",
        badge: "open",
      },
      soundhall: {
        title: "The Sound Hall",
        blurb:
          "The world's popping, played as music. Every pop anyone makes becomes a note, and the hall changes key as the Grand Tally grows.",
        badge: "open",
      },
      gallery: {
        title: "The Gallery",
        blurb:
          "Three seasons hang here: skies grown from the Tally's milestones, one pop dissected into nine instants, and the Sound Hall's nine keys made visible.",
        badge: "open",
      },
      cinema: {
        title: "The Cinema",
        blurb:
          "Three reels: the building's skies, one pop in slow motion, and the Sound Hall's keys made visible — all scored live by the hall itself.",
        badge: "open",
      },
      daily: {
        title: "The Daily Pop",
        blurb:
          "One sky a day, dealt identically to every Popper on Earth. Five calls, an honest stopwatch, and a row of squares to show for it.",
        badge: "new sky daily",
      },
      musicbox: {
        title: "The Music Box",
        blurb:
          "Hang balloons on a sixteen-step grid and the playhead pops them into a tune — always in the building's key. The whole tune fits in a link.",
        badge: "open",
      },
      bellows: {
        title: "The Bellows",
        blurb:
          "Blow into your microphone and a balloon grows until it can't take any more. Every one breaks at a different point. No microphone? Pump it by hand.",
        badge: "open",
      },
      soon: {
        title: "???",
        blurb: "Something is being assembled behind the scaffolding.",
        badge: "soon",
      },
    },
  },
  daily: {
    kicker: "The Daily Pop",
    title: "Daily #{n}",
    intro:
      "Five rounds. Each one calls a kind of balloon — pop every one of those and nothing else. The clock is honest, and a wrong pop costs {penalty} seconds.",
    sameSky: "Everyone on Earth gets this exact sky today. The next one arrives at your midnight.",
    begin: "Pop today's sky",
    keys: "Keyboard works too: Tab to the field, ← → to choose, Space to pop.",
    round: "Round {i} of {count}",
    call: "Pop every {mark} {color} balloon",
    left: "{n} left",
    miss: "+{s}s",
    colors: ["magenta", "cyan", "lemon", "coral", "violet", "mint"],
    marks: ["plain", "ringed", "striped"],
    grades: {
      green: "Clean.",
      yellow: "Close.",
      red: "Messy.",
      timeout: "The sky moved on.",
    },
    official: "That's today's sky. The first run is the one that counts.",
    practiceNote: "Practice run — today's official result stands.",
    archiveNote: "An old sky, replayed for fun. It doesn't touch your streak.",
    alreadyNote: "You've already popped today's sky. The first run is the one that counts.",
    streak: "{n}-day streak",
    streakOne: "day one of a streak",
    best: "best {t}",
    share: "Share the squares",
    shared: "Copied — paste it anywhere",
    shareFail: "The clipboard said no. The text is right there — copy it by hand.",
    next: "next sky in {t}",
    nextUp: "a new sky is up — reload to meet it",
    practice: "Practice this sky again",
    leave: "Back to the balloons",
    srCall: "Round {i}. Pop every {mark} {color} balloon. {n} of them on the field.",
    srPick: "{mark} {color} balloon, {i} of {n}",
    fieldLabel: "Daily Pop balloon field",
    aboutTitle: "A puzzle the whole planet shares",
    body: "Every calendar day the hall deals one sky. The date itself is the seed, so a Popper in Seoul and a Popper in São Paulo face the same balloons in the same places. There is no leaderboard and no sign-in: your result lives on your device, and the only way to compare is to paste the squares to a friend.",
    body2: "Every balloon popped here still lands in the Grand Tally — the wrong ones too. A pop is a pop.",
  },
  musicbox: {
    kicker: "The Music Box",
    title: "Sixteen steps, eight notes",
    intro:
      "Tap the grid to hang balloons. Press play and the playhead pops them in order, round and round. Every note is in the building's key, so nothing you make can sound wrong.",
    key: "Tuned to {name}, the key the building is in right now",
    play: "Play",
    stop: "Stop",
    tempoLabel: "Tempo",
    tempos: { slow: "slow", easy: "easy", brisk: "brisk" },
    presetsLabel: "Start from",
    presets: {
      firstLight: "First Light",
      rain: "Rain on Glass",
      stairs: "Stairs",
      heartbeat: "Heartbeat",
    },
    surprise: "Surprise me",
    clear: "Clear",
    share: "Copy the tune's link",
    shared: "Copied — the whole tune is in the link",
    shareFail: "The clipboard said no. The tune is already in the address bar — copy that.",
    received: "A Popper sent you this tune. Press play.",
    count: "{n} balloons hung",
    cell: "Step {s}, note {n}",
    gridLabel: "Music box grid, sixteen steps by eight notes. Arrow keys move, Space hangs or takes down a balloon.",
    silent: "This browser won't let the box make a sound. The grid still works — and so does the link.",
    aboutTitle: "A loop that fits in a link",
    body: "There's no save button because there's nothing to save to. The whole tune — every balloon and the tempo — is packed into twenty-three characters at the end of the address. Copy the link and the tune goes with it.",
    body2: "The box borrows the Sound Hall's key, and that key moves as the Grand Tally climbs. A tune written today will play in a different key once the world has popped enough: same melody, new weather. The playhead's pops are music, not traffic — only pops made by hand reach the Tally.",
    leave: "Back to the balloons",
  },
  bellows: {
    kicker: "The Bellows",
    title: "Blow it up",
    intro:
      "One balloon, one pair of lungs. Blow into your microphone and it grows; stop, and it slowly sighs back down. Every balloon has its own breaking point. Nobody knows where it is — including the balloon.",
    privacy: "The microphone is only listened to, here, on this device. Nothing is recorded and nothing is sent anywhere.",
    mic: "Use my microphone",
    hand: "Pump by hand",
    hold: "Hold to pump",
    micDenied: "The microphone stayed shut. Pumping by hand works just as well, and it's quieter.",
    micUnavailable: "This browser has no microphone to offer. Pumping by hand works just as well.",
    listening: "Listening — blow!",
    pumping: "Hold the button (or Space) to pump.",
    closeMic: "Close the microphone",
    breath: "breath",
    popped: "{n} popped here",
    best: "biggest: {x}",
    newBest: "A new biggest — {x}.",
    cheers: [
      "Gone.",
      "That one had a lot to say.",
      "Lungs of a champion.",
      "It knew this was coming.",
      "Rubber is temporary.",
      "The Grand Tally felt that.",
      "Somewhere, a party is one balloon short.",
    ],
    aboutTitle: "Wind, measured",
    body: "The hall listens to how loud the air in front of your microphone is, many times a second, and turns that into helium. Speaking works. Singing works. Blowing works best and looks the silliest, which is the point.",
    body2: "Every balloon that bursts here lands in the Grand Tally, the same as one popped in the Atrium. It just took more effort.",
    leave: "Back to the balloons",
  },
  cinema: {
    kicker: "The Cinema",
    title: "Now Showing",
    poster: "Three reels tonight. A few minutes each. Best with sound on — nothing plays until you say so.",
    reels: {
      skies: {
        title: "Twelve Skies",
        blurb: "the Dream Wing and Season 1, spliced into one sitting · ~3 min",
      },
      anatomy: {
        title: "Anatomy of a Pop",
        blurb: "Season 2 in the only order it has — time · ~2 min",
      },
      signals: {
        title: "Signals Made Visible",
        blurb: "Season 3, key by key up the ladder — scored by the very hall it paints · ~2 min",
      },
    },
    begin: "Start the screening",
    pause: "Pause",
    resume: "Resume",
    frame: "Cut {i} of {count}",
    endTitle: "Fin.",
    endNote: "The skies are still out there, being popped under.",
    replay: "Screen it again",
    otherReel: "Screen the other reel: {title}",
    leave: "Back to the balloons",
    silent: "This browser won't let the film have a score — it screens silent.",
    mutedHint: "Sound is muted — tap for the score",
    aboutTitle: "A projector pointed at the whole building",
    body: "The Cinema shows nothing new — that's the point. Twelve Skies runs the Dream Wing and the Gallery's first season back to back; Anatomy of a Pop plays Season 2 as the slow-motion film it always was. You've seen the rooms; this is the retrospective.",
    body2: "The score is live, not recorded: the Sound Hall plays whatever the world is popping while you watch, so no two screenings sound quite the same. When the lights come up, the balloons are one floor down.",
  },
  gallery: {
    kicker: "The Gallery",
    title: "Three seasons, one corridor",
    seasonLabel: "Season {n}",
    intro:
      "Season 1 walks the Grand Tally's ladder: nine skies, one per milestone. Season 2 turns the microscope around: one pop, dissected into nine instants. Season 3 changes the question again — in what key? — and paints the nine movements the Sound Hall plays as the number grows. Generated, not painted; an exhibition, not a mood.",
    skip: "skip to Season {n} ↓",
    hint: "walk ↓",
    plaqueNo: "No. {i} of {count}",
    plaqueSeed: "seeded by milestone {n}",
    outro: "Back to the balloons",
  },
  milestone: {
    kicker: "The world just crossed",
    sub: "pops, together, right now",
  },
  share: {
    kicker: "A pop worth keeping",
    body: "Someone popped a balloon and the world's counter landed here. Every visitor pushes it a little further.",
    yours: "This one is yours, Popper — the world's counter was here when you popped.",
    yoursCount: "{n} of the world's pops came from this very device.",
    cta: "Pop your own",
    button: "Share your pop",
  },
  passport: {
    line: "your pops: {n} · popping since {when}",
    today: "today",
  },
  missing: {
    kicker: "The Missing Hall",
    title: "This hall hasn't been built yet",
    body: "You've walked past the last door on the floor plan. Behind this scaffolding there's nothing but sky — though if you hold still, you can hear the world still popping below.",
    cta: "Back to the map",
  },
  soundhall: {
    kicker: "The Sound Hall",
    title: "Music played by strangers",
    prompt: "Best with sound on. Nothing plays until you say so.",
    begin: "Open the hall",
    unavailable: "This browser won't let the hall make a sound. Everything else still works.",
    body: "Every balloon popped anywhere in Poparium arrives here as a note. When the world is quiet the hall plays itself, so it is never silent — but the good stuff happens when other people are popping.",
    body2: "The scale is pentatonic, so no combination can sound wrong. The key shifts as the Grand Tally crosses milestones: the more the world has popped, the further the music has travelled.",
  },
  observatory: {
    kicker: "The Observatory",
    heroLabel: "pops, worldwide, since the doors opened",
    body: "Every pop anyone has ever made here, counted in one place. The number moves while you watch it.",
    loading: "Taking readings…",
    error: "The instruments are down. The Tally above is still live.",
    nowPlaying: {
      label: "The hall is playing in",
      note: "root {hz} Hz · changes key at {n}",
      noteFinal: "root {hz} Hz · the ladder's last key",
      link: "hear it in the Sound Hall →",
    },
    week: {
      title: "The Rhythm",
      sub: "How the popping breathes, day by day. This ledger is newer than the Tally, so its earliest days read zero — and it counts pops, not people; nobody signs in here.",
      chartTitle: "Pops per day",
      chartSub: "The last fourteen days · days tick over at UTC midnight",
      thisWeek: "last 7 days",
      lastWeek: "the 7 before",
    },
    standings: {
      title: "The Standings",
      sub: "Where the world pops from — as the sky sees it, not as passports say. The board is newer than the Tally, so the earliest pops flew unlabelled.",
      unknown: "Somewhere in the sky",
      you: "your pops land here",
      empty:
        "No countries on the board yet. The next pop anywhere puts its home on the map — go be a founding nation.",
      moreSuffix: "more countries on the board",
    },
  },
  about: {
    kicker: "About",
    title: "One night. One human. A swarm of AIs.",
    body: "Poparium was planned at midnight and built while its owner slept — a small showcase of what one person and a fleet of Claude agents can open overnight, running on Cloudflare's edge.",
  },
  footer: {
    line: "Made with helium and TypeScript.",
    copyright: "© 2026 Poparium",
  },
} as const;
